/**
 * 🎯 DRIVER DISCOUNT CAMPAIGNS PAGE
 * Admin creates campaigns to encourage drivers to offer discounts
 * Campaigns are pushed to mobile app and drivers can opt-in
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  FormControlLabel,
  Switch,
  Divider,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Campaign as CampaignIcon,
  CheckCircle as ActiveIcon,
  People as PeopleIcon,
  TrendingUp as TrendingIcon,
  AttachMoney as MoneyIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-hot-toast';
import driverCampaignService, {
  DriverCampaign,
  CampaignStats,
  CampaignStatus,
  DiscountType,
} from '@/services/driverCampaignService';

export const DriverCampaignsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState<DriverCampaign[]>([]);
  const [stats, setStats] = useState<CampaignStats | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCampaigns, setTotalCampaigns] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<CampaignStatus | 'all'>('all');

  // Create/Edit Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<DriverCampaign | null>(null);
  const [formData, setFormData] = useState<Partial<DriverCampaign>>({
    name: '',
    description: '',
    status: 'draft',
    startDate: new Date().toISOString(),
    endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 2 weeks
    suggestedDiscountRange: { min: 5, max: 20 },
    allowedDiscountTypes: ['percentage', 'flat'],
    targetDrivers: [], // Empty = all drivers
  });

  // View Participation Dialog
  const [participationDialogOpen, setParticipationDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<DriverCampaign | null>(null);

  useEffect(() => {
    loadCampaigns();
    loadStats();
  }, [page, rowsPerPage, filterStatus, searchQuery]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const result = await driverCampaignService.getCampaigns(
        {
          status: filterStatus,
          search: searchQuery || undefined,
        },
        page + 1,
        rowsPerPage
      );

      if (result.success && result.data) {
        setCampaigns(result.data.campaigns || []);
        setTotalCampaigns(result.data.pagination?.total || 0);
      } else {
        setCampaigns([]);
        setTotalCampaigns(0);
        if (result.error) {
          toast.error(result.error);
        }
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
      setCampaigns([]);
      setTotalCampaigns(0);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await driverCampaignService.getStats();
      if (result.success && result.data) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleCreateNew = () => {
    setEditingCampaign(null);
    setFormData({
      name: '',
      description: '',
      status: 'draft',
      startDate: new Date().toISOString(),
      endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      suggestedDiscountRange: { min: 5, max: 20 },
      allowedDiscountTypes: ['percentage', 'flat'],
      targetDrivers: [],
    });
    setDialogOpen(true);
  };

  const handleEdit = (campaign: DriverCampaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description,
      status: campaign.status,
      startDate: campaign.startDate,
      endDate: campaign.endDate,
      suggestedDiscountRange: campaign.suggestedDiscountRange,
      allowedDiscountTypes: campaign.allowedDiscountTypes,
      targetDrivers: campaign.targetDrivers,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      let result;

      if (editingCampaign) {
        result = await driverCampaignService.updateCampaign(editingCampaign.id, formData);
      } else {
        result = await driverCampaignService.createCampaign(formData as any);
      }

      if (result.success) {
        toast.success(editingCampaign ? 'Campaign updated' : 'Campaign created');
        setDialogOpen(false);
        loadCampaigns();
        loadStats();
      } else {
        toast.error(result.error || 'Failed to save campaign');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error('Failed to save campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (campaign: DriverCampaign) => {
    if (!confirm(`Delete campaign "${campaign.name}"?`)) return;

    try {
      const result = await driverCampaignService.deleteCampaign(campaign.id);
      if (result.success) {
        toast.success('Campaign deleted');
        loadCampaigns();
        loadStats();
      } else {
        toast.error(result.error || 'Failed to delete campaign');
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Failed to delete campaign');
    }
  };

  const handlePush = async (campaign: DriverCampaign) => {
    if (!confirm(`Push "${campaign.name}" to all drivers and send notifications?`)) return;

    try {
      const result = await driverCampaignService.pushCampaign(campaign.id);
      if (result.success) {
        toast.success(`Pushed to ${result.data?.driversNotified || 0} drivers`);
        loadCampaigns();
      } else {
        toast.error(result.error || 'Failed to push campaign');
      }
    } catch (error) {
      console.error('Error pushing campaign:', error);
      toast.error('Failed to push campaign');
    }
  };

  const handleActivate = async (campaign: DriverCampaign) => {
    try {
      const result = await driverCampaignService.activateCampaign(campaign.id);
      if (result.success) {
        toast.success('Campaign activated');
        loadCampaigns();
      } else {
        toast.error(result.error || 'Failed to activate campaign');
      }
    } catch (error) {
      console.error('Error activating campaign:', error);
      toast.error('Failed to activate campaign');
    }
  };

  const handleEnd = async (campaign: DriverCampaign) => {
    if (!confirm(`End campaign "${campaign.name}"? This cannot be undone.`)) return;

    try {
      const result = await driverCampaignService.endCampaign(campaign.id);
      if (result.success) {
        toast.success('Campaign ended');
        loadCampaigns();
      } else {
        toast.error(result.error || 'Failed to end campaign');
      }
    } catch (error) {
      console.error('Error ending campaign:', error);
      toast.error('Failed to end campaign');
    }
  };

  const handleViewParticipation = (campaign: DriverCampaign) => {
    setSelectedCampaign(campaign);
    setParticipationDialogOpen(true);
  };

  const getStatusColor = (status: CampaignStatus) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'default';
      case 'scheduled': return 'info';
      case 'ended': return 'error';
      case 'cancelled': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: CampaignStatus) => {
    switch (status) {
      case 'active': return <ActiveIcon fontSize="small" />;
      case 'draft': return <EditIcon fontSize="small" />;
      case 'scheduled': return <StartIcon fontSize="small" />;
      case 'ended': return <StopIcon fontSize="small" />;
      case 'cancelled': return <CancelIcon fontSize="small" />;
      default: return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const calculateParticipationRate = (campaign: DriverCampaign) => {
    // Use statistics if available (backend format), fallback to stats
    const total = campaign.statistics?.totalParticipants || campaign.stats?.totalDriversNotified || 0;
    const active = campaign.statistics?.activeParticipants || campaign.stats?.driversParticipating || 0;
    if (!total) return 0;
    return (active / total) * 100;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Page Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CampaignIcon />
              Driver Discount Campaigns
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage discount campaigns that drivers can opt into
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            Create Campaign
          </Button>
        </Box>

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="body2">
                        Total Campaigns
                      </Typography>
                      <Typography variant="h4">{stats.totalCampaigns}</Typography>
                    </Box>
                    <CampaignIcon color="primary" />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="body2">
                        Active Campaigns
                      </Typography>
                      <Typography variant="h4">{stats.activeCampaigns}</Typography>
                    </Box>
                    <ActiveIcon color="success" />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="body2">
                        Avg Participation
                      </Typography>
                      <Typography variant="h4">{stats.averageParticipationRate.toFixed(0)}%</Typography>
                    </Box>
                    <PeopleIcon color="info" />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography color="text.secondary" variant="body2">
                        Bookings Generated
                      </Typography>
                      <Typography variant="h4">{stats.totalBookingsGenerated}</Typography>
                    </Box>
                    <TrendingIcon color="warning" />
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="ended">Ended</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => { loadCampaigns(); loadStats(); }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Campaigns Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Campaign Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Discount Range</TableCell>
                <TableCell>Participation</TableCell>
                <TableCell>Bookings</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">No campaigns found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((campaign) => (
                  <TableRow key={campaign.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {campaign.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {campaign.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.status}
                        color={getStatusColor(campaign.status)}
                        size="small"
                        icon={getStatusIcon(campaign.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {campaign.suggestedDiscountRange.min}% - {campaign.suggestedDiscountRange.max}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" gutterBottom>
                          {campaign.statistics?.activeParticipants || campaign.stats?.driversParticipating || 0} / {campaign.statistics?.totalParticipants || campaign.stats?.totalDriversNotified || 0} drivers
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={calculateParticipationRate(campaign)}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {campaign.stats?.totalBookings || 0}
                      </Typography>
                      {campaign.statistics?.avgDiscount && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Avg: {campaign.statistics.avgDiscount.toFixed(1)}%
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {campaign.status === 'draft' && (
                          <Tooltip title="Push to Drivers">
                            <IconButton size="small" onClick={() => handlePush(campaign)} color="primary">
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {campaign.status === 'draft' && (
                          <Tooltip title="Activate">
                            <IconButton size="small" onClick={() => handleActivate(campaign)} color="success">
                              <StartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {campaign.status === 'active' && (
                          <Tooltip title="End Campaign">
                            <IconButton size="small" onClick={() => handleEnd(campaign)} color="error">
                              <StopIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="View Participation">
                          <IconButton size="small" onClick={() => handleViewParticipation(campaign)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(campaign)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(campaign)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCampaigns}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingCampaign ? 'Edit Campaign' : 'Create New Campaign'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                label="Campaign Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                placeholder="e.g., Christmas Special 2025"
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., Holiday season discount to attract more bookings"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Start Date"
                    value={new Date(formData.startDate || new Date())}
                    onChange={(date) => setFormData({ ...formData, startDate: date?.toISOString() || '' })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="End Date"
                    value={new Date(formData.endDate || new Date())}
                    onChange={(date) => setFormData({ ...formData, endDate: date?.toISOString() || '' })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              </Grid>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Suggested Discount Range
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label="Min %"
                      type="number"
                      value={formData.suggestedDiscountRange?.min || 5}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          suggestedDiscountRange: {
                            ...formData.suggestedDiscountRange!,
                            min: Number(e.target.value),
                          },
                        })
                      }
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Max %"
                      type="number"
                      value={formData.suggestedDiscountRange?.max || 20}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          suggestedDiscountRange: {
                            ...formData.suggestedDiscountRange!,
                            max: Number(e.target.value),
                          },
                        })
                      }
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Allowed Discount Types
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowedDiscountTypes?.includes('percentage')}
                      onChange={(e) => {
                        const types = [...(formData.allowedDiscountTypes || [])];
                        if (e.target.checked) {
                          types.push('percentage');
                        } else {
                          types.splice(types.indexOf('percentage'), 1);
                        }
                        setFormData({ ...formData, allowedDiscountTypes: types as DiscountType[] });
                      }}
                    />
                  }
                  label="Percentage Discount"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.allowedDiscountTypes?.includes('flat')}
                      onChange={(e) => {
                        const types = [...(formData.allowedDiscountTypes || [])];
                        if (e.target.checked) {
                          types.push('flat');
                        } else {
                          types.splice(types.indexOf('flat'), 1);
                        }
                        setFormData({ ...formData, allowedDiscountTypes: types as DiscountType[] });
                      }}
                    />
                  }
                  label="Flat Amount Discount"
                />
              </Box>
              <Alert severity="info">
                After creating, you can push this campaign to drivers' mobile apps and they'll receive a notification
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={20} /> : editingCampaign ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Participation Dialog */}
        <Dialog
          open={participationDialogOpen}
          onClose={() => setParticipationDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Campaign Participation: {selectedCampaign?.name}
          </DialogTitle>
          <DialogContent>
            {selectedCampaign && selectedCampaign.participation && selectedCampaign.participation.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Driver</TableCell>
                      <TableCell>Discount Type</TableCell>
                      <TableCell>Discount Value</TableCell>
                      <TableCell>Opted In</TableCell>
                      <TableCell>Bookings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedCampaign.participation.map((p) => (
                      <TableRow key={p.driverId}>
                        <TableCell>
                          <Typography variant="body2">{p.driverName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.driverCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={p.discountType} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {p.discountType === 'percentage' ? `${p.discountValue}%` : `₦${p.discountValue}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(p.optedInAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>{p.bookingsGenerated || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No drivers have participated in this campaign yet</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setParticipationDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default DriverCampaignsPage;
